import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { discoveryApi } from '../../api/discovery';
import ExploreState from './ExploreState';
import { DiscoverySection } from '../../components/content/DiscoveryContent';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { selectLandingTimeline } from './landingTimelineSelection';
import ContentShelf from '../../components/responsive/ContentShelf';
import { ExploreEraCard } from './ExploreEraCard';

export default function LandingTimeline({ entry, identity, onReady }) {
  const root = useRef(null);
  const [enabled, setEnabled] = useState(Boolean(entry?.timelineEnabled));
  const { t } = useWebLocale();
  useEffect(() => {
    if (!window.IntersectionObserver) {
      setEnabled(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setEnabled(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(root.current);
    return () => observer.disconnect();
  }, []);
  const query = useQuery({
    queryKey: ['web-parity', 'timeline-preview', identity],
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      const results = await Promise.allSettled(
        [1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020].map((decade) =>
          discoveryApi.timeline(
            { decade, size: 6, ...(decade === 1950 ? { section: 'rediscovered' } : {}) },
            signal,
          ),
        ),
      );
      if (signal.aborted) throw new Error('Timeline preview cancelled');
      const timelines = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);
      if (!timelines.length) throw new Error('Timeline preview unavailable');
      return {
        items: selectLandingTimeline(timelines),
        partial: timelines.length < results.length,
      };
    },
  });
  useEffect(() => {
    if (enabled && entry?.current()) entry.timelineEnabled = true;
    onReady?.(enabled && !query.isPending && !query.isFetching);
  }, [enabled, entry, onReady, query.isPending, query.isFetching]);
  return (
    <div ref={root}>
      <DiscoverySection inlineMore title={t('albumsByEra')} to="/explore/timeline">
        <ExploreState query={query}>
          {query.data?.items?.length ? (
            <ContentShelf label={t('albumsByEra')}>
              {(query.data?.items || []).map((item) => (
                <ExploreEraCard key={item.album.id} item={item} />
              ))}
            </ContentShelf>
          ) : (
            <p className="pp-secondary">{t('empty')}</p>
          )}
          {query.data?.partial && (
            <p role="status">
              {t('partialContent')}{' '}
              <button
                className="pp-action"
                disabled={query.isFetching}
                onClick={() => query.refetch({ cancelRefetch: false })}
              >
                {t('retry')}
              </button>
            </p>
          )}
        </ExploreState>
      </DiscoverySection>
    </div>
  );
}
